import { formatCentsAsDollars } from '@/src/common/utils/general'
import { randomUUID } from '@/src/common/utils/randomUUID'
import { useCardAccount } from '@/src/experiences/card-accounts/hooks'
import type { OnetimePayReviewComponentType } from '@/src/experiences/card-payments/screens/OnetimePayReview'
import { isErrorResponse, MesaApi } from '@acre-rewards/lib'
import { ComponentProps, useState } from 'react'
import { SchedulePaymentsApi } from '@/src/experiences/card-payments/store/api'
import { formatIsoDateStringForPaymentSchedule } from '@/src/experiences/card-payments/formatIsoDateStringForPaymentSchedule'

function useCardAccountId() {
  return useCardAccount().data?.[0]?.id
}

export const PlatformOnetimePayReview = ((props) => {
  const cardAccountId = useCardAccountId()
  return cardAccountId ? (
    <PlatformOnetimePayReviewWithCardAccount cardAccountId={cardAccountId} {...props} />
  ) : (
    <props.OnetimePayReviewComponent {...props} loading />
  )
}) satisfies OnetimePayReviewComponentType

function PlatformOnetimePayReviewWithCardAccount(
  props: ComponentProps<typeof PlatformOnetimePayReview> & {
    cardAccountId: NonNullable<ReturnType<typeof useCardAccountId>>
  },
) {
  const { onetimePaymentStore, cardAccountId } = props
  const [createPaymentSchedule, { isLoading }] = SchedulePaymentsApi.endpoints[
    'POST /card/:cardAccountId/payments/schedules'
  ].useMutation({
    fixedCacheKey: useState(randomUUID)[0],
  })

  return (
    <props.OnetimePayReviewComponent
      {...props}
      loading={isLoading}
      createOneTimePayment={() => {
        void (async () => {
          try {
            if (!onetimePaymentStore.account) {
              props.alertPaymentProcessingError(`The payment cannot be scheduled this time`)
              throw new Error(`Payment account missing when scheduling one-time payment`)
            }

            if (!onetimePaymentStore.account.profileId) {
              props.alertPaymentProcessingError(
                `${onetimePaymentStore.account.name} ${onetimePaymentStore.account.last4} cannot be used to schedule a payment at this time`,
              )
              throw new Error(`Missing profileId for payment`, {
                cause: onetimePaymentStore.account,
              })
            }

            const amountCents = Number(onetimePaymentStore.paymentAmount)

            if (!amountCents) {
              props.alertPaymentProcessingError(
                isNaN(amountCents)
                  ? 'Payment amount is invalid'
                  : `Cannot schedule a payment amount of ${formatCentsAsDollars(amountCents)}`,
              )
              throw new Error(`Missing payment amount`, {
                cause: onetimePaymentStore.paymentAmount,
              })
            }

            if (!onetimePaymentStore.paymentDate) {
              props.alertPaymentProcessingError(`The payment cannot be scheduled this time`)
              throw new Error(`Missing payment date`, { cause: onetimePaymentStore.paymentDate })
            }

            const processDateTime = formatIsoDateStringForPaymentSchedule(
              onetimePaymentStore.paymentDate,
              () => props.alertPaymentProcessingError(`Payment cannot be scheduled for this time`),
            )

            const { error } = await createPaymentSchedule({
              params: { cardAccountId },
              body: {
                type: 'single',
                profileId: onetimePaymentStore.account.profileId,
                amountType: (() => {
                  switch (onetimePaymentStore.paymentType) {
                    case 'Minimum Payment':
                      return 'min'
                    case 'Statement Balance':
                      return 'statement'
                    case 'Current Balance':
                      return 'total'
                    case 'Other Amount':
                      return 'static'
                    default:
                      throw new Error(`Invalid payment type`, {
                        cause: onetimePaymentStore.paymentType,
                      })
                  }
                })(),
                amountCents,
                processDateTime,
              },
            })

            if (error) {
              if (
                isErrorResponse(error, MesaApi['POST /card/:cardAccountId/payments/schedules']) &&
                error.status === 422
              ) {
                props.alertPaymentProcessingError(error.body.message)
                return
              }

              props.alertPaymentProcessingError('One-time payment cannot be scheduled')
              throw new Error('One-time payment scheduling failed', { cause: error })
            }

            props.onSuccess()
          } catch (error) {
            props.logError('Could not schedule one-time payment', error)
          }
        })()
      }}
    />
  )
}
